import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  Response,
  BaseTransaction,
  Pagination,
  TransactionWarning,
  TransactionWarningActionEnum,
} from '@ui/src';
import {
  CustomerScore,
  CustomerScoreQueuedProps,
  CustomerScoreUpdateStatus,
  OngoingMonitring,
} from '../../../../customers/types';
import { CorporateAmlGeneralInfo } from '../../../types';
import { corporateCustomersApi } from '../../services/corporate';
import { customersComponentApi } from '../../../../customers/store/services/customers';
import {
  AccountsCurrencies,
  AccountsFlows,
  DictionaryItem,
  ExpectedFundsOrigins,
} from '../../../../new-application/types';

export interface CorporateCustomerAmlState {
  amlGeneralInfo: {
    isLoading: boolean;
    values?: CorporateAmlGeneralInfo;
  };
  aml?: CustomerScore;
  amlUpdateStatus?: CustomerScoreUpdateStatus;
  lastCalculatingAml?: CustomerScore;
  amlTransactions?: BaseTransaction[];
  pagination?: Pagination;
  nextReviewDate?: string;
  ongoingMonitoring?: OngoingMonitring[];
  ongoingMonitoringPagination?: Pagination;
  ongoingMonitoringItem?: OngoingMonitring;
  amlChangeLogs?: CustomerScore[];
  amlChangeLogsPagination?: Pagination;
  relationshipToTheCustomerDictionary?: DictionaryItem[];
  sourceOfWealth?: DictionaryItem[];
  businessActivities?: DictionaryItem[];
  customerMainClients?: DictionaryItem[];
  salesChannels?: DictionaryItem[];
  customerSourceOfFunds?: DictionaryItem[];
  customerSourceOfWealth?: DictionaryItem[];
  annualIncomes?: DictionaryItem[];
  accountsCurrencies?: AccountsCurrencies[];
  accountsFlows?: AccountsFlows[];
  expectedFundsOrigins?: ExpectedFundsOrigins[];
  typeOfAccounts?: DictionaryItem[];
}

const initialState: CorporateCustomerAmlState = {
  amlGeneralInfo: {
    isLoading: false,
  },
};

export const corporateCustomerAmlSlice = createSlice({
  name: 'corporateCustomerAml',
  initialState,
  reducers: {
    getAmlGeneralInfo: (state) => {
      state.amlGeneralInfo.isLoading = true;
    },
    getAmlGeneralInfoSuccess: (
      state,
      action: PayloadAction<CorporateAmlGeneralInfo>,
    ) => {
      state.amlGeneralInfo.isLoading = false;
      state.amlGeneralInfo.values = action.payload;
    },
    clearAmlState: (state) => {
      state.amlGeneralInfo.values = undefined;
      state.aml = undefined;
      state.amlUpdateStatus = undefined;
      state.lastCalculatingAml = undefined;
      state.ongoingMonitoring = undefined;
      state.ongoingMonitoringPagination = undefined;
      state.ongoingMonitoringItem = undefined;
      state.accountsCurrencies = undefined;
      state.accountsFlows = undefined;
      state.expectedFundsOrigins = undefined;
    },
    clearAmlChangeLogs: (state) => {
      state.amlChangeLogs = undefined;
      state.amlChangeLogsPagination = undefined;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      corporateCustomersApi.endpoints.getCorporateOngoingWarning.matchFulfilled,
      (
        state,
        action: PayloadAction<{ data: TransactionWarning }> & {
          meta: {
            arg: {
              originalArgs: {
                id: string;
                warningId: string;
              };
            };
          };
        },
      ) => {
        const nextWarnings = state.ongoingMonitoring?.map((item) => {
          const result = { ...item };

          if (item.id === action.meta.arg.originalArgs.id) {
            const nextGroupWarnings = item.warnings?.edges?.map((warning) => {
              const warningResult = { ...warning };

              if (
                warningResult.node.id === action.meta.arg.originalArgs.warningId
              ) {
                warningResult.node.blacklistDoc =
                  action.payload.data.blacklistDoc;
                warningResult.node.blacklistItem =
                  action.payload.data.blacklistItem;
              }

              return warningResult;
            });

            result.warnings = {
              edges: nextGroupWarnings,
              pageInfo: result.warnings.pageInfo,
            };
          }

          return result;
        });

        state.ongoingMonitoring = nextWarnings;
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomersTypeOfAccountsDictionary
        .matchFulfilled,
      (state, action: PayloadAction<Response<DictionaryItem>>) => {
        state.typeOfAccounts = action.payload.data.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomersAccountsCurrencies
        .matchFulfilled,
      (state, action: PayloadAction<Response<AccountsCurrencies>>) => {
        state.accountsCurrencies = action.payload.data.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.createCustomersAccountsCurrency
        .matchFulfilled,
      (state, action: PayloadAction<{ data: AccountsCurrencies }>) => {
        let nextAccountsCurrencies: AccountsCurrencies[] = [];

        if (state.accountsCurrencies) {
          nextAccountsCurrencies = ([] as AccountsCurrencies[]).concat(
            state.accountsCurrencies,
          );
        }

        nextAccountsCurrencies.push(action.payload.data);

        state.accountsCurrencies = nextAccountsCurrencies;
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.updateCustomersAccountsCurrency
        .matchFulfilled,
      (state, action: PayloadAction<{ data: AccountsCurrencies }>) => {
        if (state.accountsCurrencies) {
          state.accountsCurrencies = state.accountsCurrencies?.map((item) => {
            let result = { ...item };

            if (result.id === action.payload.data.id) {
              result = action.payload.data;
            }

            return result;
          });
        }
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.deleteCustomersAccountsCurrency
        .matchFulfilled,
      (state, action: PayloadAction<{ data: AccountsCurrencies }>) => {
        if (state.accountsCurrencies) {
          state.accountsCurrencies = state.accountsCurrencies?.filter(
            (item) => item.id !== action.payload.data.id,
          );
        }
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomersAccountsFlows.matchFulfilled,
      (state, action: PayloadAction<Response<AccountsFlows>>) => {
        state.accountsFlows = action.payload.data.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.createCustomersAccountsFlow
        .matchFulfilled,
      (state, action: PayloadAction<{ data: AccountsFlows }>) => {
        let nextAccountsFlows: AccountsFlows[] = [];

        if (state.accountsFlows) {
          nextAccountsFlows = ([] as AccountsFlows[]).concat(
            state.accountsFlows,
          );
        }

        nextAccountsFlows.push(action.payload.data);

        state.accountsFlows = nextAccountsFlows;
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.updateCustomersAccountsFlow
        .matchFulfilled,
      (state, action: PayloadAction<{ data: AccountsFlows }>) => {
        if (state.accountsFlows) {
          state.accountsFlows = state.accountsFlows?.map((item) => {
            let result = { ...item };

            if (result.id === action.payload.data.id) {
              result = action.payload.data;
            }

            return result;
          });
        }
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.deleteCustomersAccountsFlow
        .matchFulfilled,
      (state, action: PayloadAction<{ data: AccountsFlows }>) => {
        if (state.accountsFlows) {
          state.accountsFlows = state.accountsFlows?.filter(
            (item) => item.id !== action.payload.data.id,
          );
        }
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomersExpectedFundsOrigins
        .matchFulfilled,
      (state, action: PayloadAction<Response<ExpectedFundsOrigins>>) => {
        state.expectedFundsOrigins = action.payload.data.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.createCustomersExpectedFundsOrigins
        .matchFulfilled,
      (state, action: PayloadAction<{ data: ExpectedFundsOrigins }>) => {
        let nextExpectedFundsOrigins: ExpectedFundsOrigins[] = [];

        if (state.expectedFundsOrigins) {
          nextExpectedFundsOrigins = ([] as ExpectedFundsOrigins[]).concat(
            state.expectedFundsOrigins,
          );
        }

        nextExpectedFundsOrigins.push(action.payload.data);

        state.expectedFundsOrigins = nextExpectedFundsOrigins;
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.updateCustomersExpectedFundsOrigins
        .matchFulfilled,
      (state, action: PayloadAction<{ data: ExpectedFundsOrigins }>) => {
        if (state.expectedFundsOrigins) {
          state.expectedFundsOrigins = state.expectedFundsOrigins?.map(
            (item) => {
              let result = { ...item };

              if (result.id === action.payload.data.id) {
                result = action.payload.data;
              }

              return result;
            },
          );
        }
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.deleteCustomersExptectedFundsOrigins
        .matchFulfilled,
      (state, action: PayloadAction<{ data: ExpectedFundsOrigins }>) => {
        if (state.expectedFundsOrigins) {
          state.expectedFundsOrigins = state.expectedFundsOrigins?.filter(
            (item) => item.id !== action.payload.data.id,
          );
        }
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomersBusinessActivitiesDictionary
        .matchFulfilled,
      (state, action: PayloadAction<Response<DictionaryItem>>) => {
        const nextBusinessActivities = action.payload.data.edges?.map(
          (item) => item.node,
        );

        nextBusinessActivities.unshift({
          value: 'None of the listed activities',
          id: 'NONE',
          dictionaryId: nextBusinessActivities[0].id,
          createdAt: nextBusinessActivities[0].createdAt,
          updatedAt: nextBusinessActivities[0].updatedAt,
        });

        state.businessActivities = nextBusinessActivities;
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomersSalesChannelsDictionary
        .matchFulfilled,
      (state, action: PayloadAction<Response<DictionaryItem>>) => {
        state.salesChannels = action.payload.data.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomersSourceOfWealth.matchFulfilled,
      (state, action: PayloadAction<Response<DictionaryItem>>) => {
        state.customerSourceOfWealth = action.payload.data.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getRegulatedCompanyMainClientsDictionary
        .matchFulfilled,
      (state, action: PayloadAction<Response<DictionaryItem>>) => {
        state.customerMainClients = action.payload.data.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints
        .getCustomersRelationShipToCustomerDictionary.matchFulfilled,
      (state, action: PayloadAction<Response<DictionaryItem>>) => {
        state.relationshipToTheCustomerDictionary =
          action.payload.data.edges?.map((item) => item.node);
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomersSourceOfFunds.matchFulfilled,
      (state, action: PayloadAction<Response<DictionaryItem>>) => {
        state.customerSourceOfFunds = action.payload.data.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints
        .getCustomersSourceOfWealthForCorporateDictionary.matchFulfilled,
      (state, action: PayloadAction<Response<DictionaryItem>>) => {
        state.sourceOfWealth = action.payload.data.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomersAnnualIncomeDictionary
        .matchFulfilled,
      (state, action: PayloadAction<Response<DictionaryItem>>) => {
        state.annualIncomes = action.payload.data.edges?.map(
          (item) => item.node,
        );
      },
    );
    builder.addMatcher(
      corporateCustomersApi.endpoints.getCorporatelUserScore.matchFulfilled,
      (state, action: PayloadAction<{ data: CustomerScore }>) => {
        state.aml = action.payload.data;

        if (action?.payload?.data?.createdAt) {
          const date = new Date(action.payload.data.createdAt);

          const nextReviewDate = new Date(
            date.setFullYear(date.getFullYear() + 1),
          );

          state.nextReviewDate = nextReviewDate.toISOString();
        }
      },
    );
    builder.addMatcher(
      corporateCustomersApi.endpoints.getCorporatelUserScore.matchRejected,
      (state) => {
        state.aml = undefined;
      },
    );

    builder.addMatcher(
      corporateCustomersApi.endpoints.getCorporateAmlUpdatingItem
        .matchFulfilled,
      (state, action: PayloadAction<{ data: CustomerScoreQueuedProps }>) => {
        state.amlUpdateStatus = action.payload.data.status;

        if (action.payload.data.status === CustomerScoreUpdateStatus.FAIL) {
          localStorage.removeItem('amlCorporateUpdateId');
        }

        if (action.payload.data.status === CustomerScoreUpdateStatus.SUCCESS) {
          localStorage.removeItem('amlCorporateUpdateId');
        }
      },
    );
    builder.addMatcher(
      corporateCustomersApi.endpoints.getCorporateLastCalculatingScore
        .matchFulfilled,
      (state, action: PayloadAction<Response<CustomerScore>>) => {
        state.lastCalculatingAml = action.payload.data.edges[0].node;
      },
    );
    builder.addMatcher(
      corporateCustomersApi.endpoints.createCorporateUserScore.matchFulfilled,
      (state, action: PayloadAction<{ data: CustomerScoreQueuedProps }>) => {
        state.amlUpdateStatus = action.payload.data.status;
        localStorage.setItem('amlCorporateUpdateId', action.payload.data.id);
      },
    );
    builder.addMatcher(
      corporateCustomersApi.endpoints.getCorporateTransactionsWithWarnings
        .matchFulfilled,
      (state, action: PayloadAction<Response<BaseTransaction>>) => {
        state.amlTransactions = action.payload.data.edges
          .filter((item) => item.node)
          ?.map((item) => item.node);

        state.pagination = action.payload.data.pageInfo;
      },
    );
    builder.addMatcher(
      corporateCustomersApi.endpoints.getCorporateOngoingMonitoringItem
        .matchFulfilled,
      (state, action: PayloadAction<{ data: OngoingMonitring }>) => {
        state.ongoingMonitoringItem = action.payload.data;
      },
    );
    builder.addMatcher(
      corporateCustomersApi.endpoints.getCorporateOngoingMonitoring
        .matchFulfilled,
      (state, action: PayloadAction<Response<OngoingMonitring>>) => {
        state.ongoingMonitoring = action?.payload?.data?.edges?.map((item) => ({
          ...item.node,
          blocklistNumber: '-',
          matches: '-',
          result: '-',
        }));
        state.ongoingMonitoringPagination = action?.payload?.data?.pageInfo;
      },
    );
    builder.addMatcher(
      corporateCustomersApi.endpoints.applyCorporateOngoingWarningAction
        .matchFulfilled,
      (state, action: PayloadAction<{ data: TransactionWarning }>) => {
        state.ongoingMonitoring = state.ongoingMonitoring?.map((item) => {
          const result = { ...item };

          const nextWarnings = result.warnings?.edges?.map((warning) => {
            let resultWarning = { ...warning };

            if (resultWarning.node.id === action.payload.data.id) {
              resultWarning = { node: action.payload.data };
            }

            return resultWarning;
          });

          result.warnings = {
            edges: nextWarnings,
            pageInfo: result.warnings?.pageInfo,
          };

          return result;
        });

        if (state.ongoingMonitoringItem) {
          const nextOngoingWarningItem = { ...state.ongoingMonitoringItem };

          const nextOngoingWarningItemWarnings =
            nextOngoingWarningItem.warnings?.edges?.map((item) => {
              let resultWarning = { ...item };

              if (resultWarning.node.id === action.payload.data.id) {
                resultWarning = { node: action.payload.data };
              }

              return resultWarning;
            });

          nextOngoingWarningItem.warnings = {
            edges: nextOngoingWarningItemWarnings,
            pageInfo: nextOngoingWarningItem.warnings?.pageInfo,
          };

          state.ongoingMonitoringItem = nextOngoingWarningItem;
        }
      },
    );
    builder.addMatcher(
      customersComponentApi.endpoints.getCustomersAMLScoresHistory
        .matchFulfilled,
      (
        state,
        action: PayloadAction<Response<CustomerScore>> & {
          meta: { arg: { originalArgs: { query?: string } } };
        },
      ) => {
        if (state.aml) {
          let nextAmlChangeLogs: CustomerScore[] = [];

          if (
            action.meta.arg.originalArgs?.query?.includes('cursor') &&
            state.amlChangeLogs
          ) {
            nextAmlChangeLogs = ([] as CustomerScore[]).concat(
              state.amlChangeLogs,
            );
          }

          action.payload.data.edges?.forEach((item) => {
            nextAmlChangeLogs.push(item.node);
          });

          state.amlChangeLogs = nextAmlChangeLogs?.map((item, index) => ({
            ...item,
            order: index + 1,
          }));

          state.amlChangeLogsPagination = action.payload.data.pageInfo;
        }
      },
    );
    builder.addMatcher(
      corporateCustomersApi.endpoints.resetCorporateWarningGroupAction
        .matchFulfilled,
      (state, action: PayloadAction<{ data: TransactionWarning }>) => {
        state.ongoingMonitoring = state.ongoingMonitoring?.map((item) => {
          const result = { ...item };

          const nextWarnings = result.warnings?.edges?.map((warning) => {
            let resultWarning = { ...warning };

            if (resultWarning.node.id === action.payload.data.id) {
              resultWarning = {
                node: {
                  ...action.payload.data,
                  action: undefined,
                  matchStatus: undefined,
                  comment: undefined,
                },
              };
            }

            return resultWarning;
          });

          result.warnings = {
            edges: nextWarnings,
            pageInfo: result.warnings?.pageInfo,
          };

          return result;
        });

        if (state.ongoingMonitoringItem) {
          const nextOngoingWarningItem = { ...state.ongoingMonitoringItem };

          const nextOngoingWarningItemWarnings =
            nextOngoingWarningItem.warnings?.edges?.map((item) => {
              let resultWarning = { ...item };

              if (resultWarning.node.id === action.payload.data.id) {
                resultWarning = { node: action.payload.data };
              }

              return resultWarning;
            });

          nextOngoingWarningItem.warnings = {
            edges: nextOngoingWarningItemWarnings,
            pageInfo: nextOngoingWarningItem.warnings?.pageInfo,
          };

          state.ongoingMonitoringItem = nextOngoingWarningItem;
        }
      },
    );
    builder.addMatcher(
      corporateCustomersApi.endpoints.getCorporateWarningGroupWithPagination
        .matchFulfilled,
      (
        state,
        action: PayloadAction<Response<TransactionWarning>> & {
          meta: {
            arg: {
              originalArgs: {
                warningGroupId: string;
              };
            };
          };
        },
      ) => {
        if (state.ongoingMonitoringItem) {
          const result = { ...state.ongoingMonitoringItem };

          if (result.id === action.meta.arg.originalArgs.warningGroupId) {
            const warningResult = { ...result.warnings };

            warningResult.pageInfo = action.payload.data.pageInfo;

            const currentWarnings = result.warnings.edges?.map(
              (warning) => warning.node,
            );

            const nextWarnings = ([] as TransactionWarning[]).concat(
              currentWarnings,
            );

            action?.payload?.data?.edges?.forEach((warning) =>
              nextWarnings.push(warning.node),
            );

            warningResult.edges = nextWarnings?.map((warning) => ({
              node: warning,
            }));

            result.warnings = warningResult;
          }

          state.ongoingMonitoringItem = result;
        }
      },
    );
    builder.addMatcher(
      corporateCustomersApi.endpoints.batchApplyCorporateOngoingWarningAction
        .matchFulfilled,
      (
        state,
        action: {
          meta: {
            arg: {
              originalArgs: {
                body: {
                  action: string;
                  comment: string;
                  matchStatus?: string;
                  warningGroupId: string;
                };
              };
            };
          };
        },
      ) => {
        state.ongoingMonitoring = state.ongoingMonitoring?.map(
          (item: OngoingMonitring) => {
            const result = { ...item };

            if (
              result.id === action.meta.arg.originalArgs.body.warningGroupId
            ) {
              const nextWarnings = result.warnings?.edges?.map(
                (warning: { node: TransactionWarning }) => {
                  let resultWarning = { ...warning };

                  resultWarning = {
                    node: {
                      ...resultWarning.node,
                      action: action.meta.arg.originalArgs.body
                        .action as TransactionWarningActionEnum,
                      comment: action.meta.arg.originalArgs.body.comment,
                      matchStatus:
                        action?.meta?.arg?.originalArgs?.body?.matchStatus,
                    },
                  };

                  return resultWarning;
                },
              );

              result.warnings = {
                edges: nextWarnings,
                pageInfo: result.warnings?.pageInfo,
              };
            }

            return result;
          },
        );

        if (state.ongoingMonitoringItem) {
          const nextOngoingWarningItem = { ...state.ongoingMonitoringItem };

          const nextOngoingWarningItemWarnings =
            nextOngoingWarningItem.warnings?.edges?.map((item) => {
              let resultWarning = { ...item };

              resultWarning = {
                node: {
                  ...resultWarning.node,
                  action: action.meta.arg.originalArgs.body
                    .action as TransactionWarningActionEnum,
                  comment: action.meta.arg.originalArgs.body.comment,
                  matchStatus: action.meta.arg.originalArgs.body.matchStatus,
                },
              };

              return resultWarning;
            });

          nextOngoingWarningItem.warnings = {
            edges: nextOngoingWarningItemWarnings,
            pageInfo: nextOngoingWarningItem.warnings?.pageInfo,
          };

          state.ongoingMonitoringItem = nextOngoingWarningItem;
        }
      },
    );
  },
});

export const {
  getAmlGeneralInfo,
  getAmlGeneralInfoSuccess,
  clearAmlState,
  clearAmlChangeLogs,
} = corporateCustomerAmlSlice.actions;

export default corporateCustomerAmlSlice.reducer;
